import type * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { CursorPage } from '@/hooks/use-cursor-page';

/**
 * The pager every cursor-paginated list in the panel uses.
 *
 * ⚠️ **No page count, and no "of N".** The API returns `{ nextCursor, hasMore }`
 * and no total anywhere (CLAUDE.md §5), so a control offering page numbers would
 * have to invent the denominator. "Page 3" is honest — the admin has pressed
 * next twice — where "page 3 of 12" is not.
 */
export function CursorFooter<T>({
  page,
  note,
}: {
  page: CursorPage<T>;
  /** Anything worth saying about the rows themselves, shown on the left. */
  note?: React.ReactNode;
}): React.JSX.Element {
  return (
    <>
      <span>{note}</span>
      <span className="flex items-center gap-2">
        <span className="text-ink-faint" data-numeric>
          Page {page.pageNumber}
        </span>
        <Button
          size="sm"
          variant="ghost"
          disabled={!page.hasPrevious}
          onClick={page.previous}
          aria-label="Previous page"
        >
          <ChevronLeft aria-hidden="true" />
          Previous
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={!page.hasNext}
          onClick={page.next}
          aria-label="Next page"
        >
          Next
          <ChevronRight aria-hidden="true" />
        </Button>
      </span>
    </>
  );
}
